import React, { useState, useRef, useEffect, useCallback } from 'react';
import { C, F, btn, inp } from '../../config/styles';
import { isVideoMedia } from '../../utils/products';
import { presetRatio, centeredRect, moveRect, resizeRect, roundRect, outputSize, withExt, formatTime, fitsRatio } from '../../utils/crop';
import { cropImage, cropVideo, cropToPreset, mediaSize } from '../../utils/mediaCrop';
import { enqueueCrop } from '../../utils/cropJobs';

const STAGE_W = 720, STAGE_H = 440;
const FREE = { id: '__free', label: 'Free' };

// Crop a Studio image or video to one of the workspace's presets (Settings → Crop Presets) or
// free-form; videos can be trimmed in the same pass. Always works from the untouched original
// when there is one, so re-cropping never compounds quality loss. onApply(file, crop) does
// the upload; this only produces the file.
// index/total/onNavigate let the parent step through a product's media without closing;
// presetId carries the last-used preset across items, so a batch gets the same crop.
// Videos don't encode here: Apply queues the job (utils/cropJobs) and the window moves on, so
// nobody waits on a multi-minute encode — productId/productLabel say where the result goes.
export default function CropModal({ media, presets = [], onApply, onClose, index = 0, total = 1, onNavigate, presetId, onPresetChange, productId, productLabel }) {
  const isVideo = isVideoMedia(media);
  const source  = media.original || media;
  const prior   = media.original ? media.crop : null;   // reopen where the last crop left off
  const allPresets = [FREE, ...presets];

  const [preset, setPreset] = useState(() =>
    allPresets.find(p => p.id === prior?.presetId) || allPresets.find(p => p.id === presetId) || presets[0] || FREE);
  const [natural, setNatural] = useState(null);       // { W, H } of the source
  const [rect, setRect] = useState(null);
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState({ start: prior?.start || 0, end: prior?.end ?? null });
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(null);             // null | 'loading' | 'encoding' | 'image' | 'uploading'
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const drag = useRef(null);

  const ratio = presetRatio(preset);
  const scale = natural ? Math.min(STAGE_W / natural.W, STAGE_H / natural.H, 1) : 1;
  const end = range.end ?? duration;

  const onMeta = (W, H) => {
    setNatural({ W, H });
    setRect(prior?.rect && prior.rect.x + prior.rect.w <= W && prior.rect.y + prior.rect.h <= H
      ? prior.rect : centeredRect(W, H, presetRatio(preset)));
  };

  const choosePreset = (p) => {
    setPreset(p);
    onPresetChange?.(p.id);
    if (natural) setRect(centeredRect(natural.W, natural.H, presetRatio(p)));
  };

  // ── Dragging: move the box, or one of its corners ──────────────────────────
  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !natural) return;
    const dx = (e.clientX - d.px) / scale, dy = (e.clientY - d.py) / scale;
    setRect(d.corner
      ? resizeRect(d.start, d.corner, dx, dy, natural.W, natural.H, ratio)
      : moveRect(d.start, dx, dy, natural.W, natural.H));
  }, [natural, scale, ratio]);

  const onPointerUp = useCallback(() => { drag.current = null; }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => { window.removeEventListener('pointermove', onPointerMove); window.removeEventListener('pointerup', onPointerUp); };
  }, [onPointerMove, onPointerUp]);

  const startDrag = (e, corner = null) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { px: e.clientX, py: e.clientY, start: rect, corner };
  };

  // ── Video preview loops inside the trimmed range ───────────────────────────
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime >= end || v.currentTime < range.start - 0.05) v.currentTime = range.start;
    setTime(v.currentTime);
  };
  const seek = (t) => { if (videoRef.current) videoRef.current.currentTime = t; setTime(t); };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { if (v.currentTime >= end || v.currentTime < range.start) v.currentTime = range.start; v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const setStart = (t) => setRange(r => ({ ...r, start: Math.max(0, Math.min(t, (r.end ?? duration) - 0.1)) }));
  const setEnd   = (t) => setRange(r => ({ ...r, end: Math.min(duration, Math.max(t, r.start + 0.1)) }));

  // ── Apply ──────────────────────────────────────────────────────────────────
  const r   = rect ? roundRect(rect, isVideo) : null;
  const out = r ? outputSize(r, preset, isVideo) : null;
  const upscaling = r && out && out.w > r.w * 1.05;
  const trimmed = isVideo && (range.start > 0.05 || (range.end != null && range.end < duration - 0.05));
  const untouched = r && natural && r.x === 0 && r.y === 0 && r.w >= natural.W - 1 && r.h >= natural.H - 1
    && out.w === r.w && out.h === r.h && !trimmed;

  const canPrev = !!onNavigate && index > 0;
  const canNext = !!onNavigate && index < total - 1;
  const go = (dir) => { if (!busy && (dir < 0 ? canPrev : canNext)) onNavigate(dir); };

  // ← / → step through the product's media (not while typing a trim time).
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.('input, textarea')) return;
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const apply = async (advance = false) => {
    setError(''); setProgress(0);
    videoRef.current?.pause(); setPlaying(false);
    const crop = {
      presetId: preset.id, rect: r, out,
      ...(isVideo ? { start: range.start, end: trimmed ? end : null } : {}),
    };
    if (isVideo) {
      const trim = { start: range.start, end: trimmed ? end : null };
      const srcUrl = source.url, filename = withExt(source.filename, 'mp4');
      enqueueCrop({
        productId, mediaUrl: media.url,
        label: `${productLabel || 'Video'} — ${source.filename || 'video'}`,
        make: async (cb) => {
          const res = await cropVideo(srcUrl, r, out, trim, cb);
          return { file: new File([res.blob], filename, { type: res.blob.type }), crop };
        },
      });
      if (advance && canNext) onNavigate(1); else onClose();
      return;
    }
    try {
      setBusy('image');
      const result = await cropImage(source.url, r, out);
      setBusy('uploading');
      const filename = withExt(source.filename, result.ext);
      const file = new File([result.blob], filename, { type: result.blob.type });
      await onApply(file, crop, advance);
    } catch (err) {
      setError(err.message || String(err));
      setBusy(null);
    }
  };

  const stageW = natural ? natural.W * scale : STAGE_W;
  const stageH = natural ? natural.H * scale : STAGE_H;
  const handle = (corner, pos) => (
    <div onPointerDown={e => startDrag(e, corner)}
      style={{ position: 'absolute', width: 14, height: 14, background: '#fff', border: `1px solid ${C.text}`, borderRadius: 3, cursor: `${corner}-resize`, ...pos }} />
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.white, borderRadius: 14, padding: 24, width: STAGE_W + 48, maxWidth: '96vw', maxHeight: '94vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: F.body }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontFamily: F.display, fontSize: 18, color: C.text }}>{isVideo ? 'Crop & trim video' : 'Crop image'}</div>
          {total > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginRight: 14 }}>
              <button onClick={() => go(-1)} disabled={!canPrev || !!busy} title="Previous (←)"
                style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px' }), opacity: canPrev ? 1 : 0.3 }}>← Prev</button>
              <span style={{ fontSize: 12, color: C.muted, fontFamily: F.mono }}>{index + 1} / {total}</span>
              <button onClick={() => go(1)} disabled={!canNext || !!busy} title="Next (→)"
                style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px' }), opacity: canNext ? 1 : 0.3 }}>Next →</button>
            </div>
          )}
          <button onClick={onClose} disabled={!!busy} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {allPresets.map(p => {
            const active = p.id === preset.id;
            const sub = p.id === FREE.id ? '' : `${p.ratioW}:${p.ratioH}${Number(p.width) > 0 ? ` · ${p.width}px` : ''}`;
            return (
              <button key={p.id} onClick={() => choosePreset(p)} disabled={!!busy}
                style={{ ...btn('ghost', { fontSize: 12, padding: '5px 12px', border: `1px solid ${active ? C.text : C.e8}`, borderRadius: 6 }), background: active ? C.text : C.white, color: active ? C.white : C.text }}>
                {p.label}{sub && <span style={{ opacity: 0.6, marginLeft: 6 }}>{sub}</span>}
              </button>
            );
          })}
        </div>

        <div style={{ width: STAGE_W, maxWidth: '100%', height: STAGE_H, background: '#111', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ position: 'relative', width: stageW, height: stageH, overflow: 'hidden', touchAction: 'none' }}>
            {isVideo ? (
              <video ref={videoRef} src={source.url} muted playsInline preload="auto"
                onLoadedMetadata={e => { onMeta(e.target.videoWidth, e.target.videoHeight); setDuration(e.target.duration); if (range.start) e.target.currentTime = range.start; }}
                onTimeUpdate={onTimeUpdate} onPause={() => setPlaying(false)}
                style={{ width: stageW, height: stageH, display: 'block' }} />
            ) : (
              <img src={source.url} alt={source.filename}
                onLoad={e => onMeta(e.target.naturalWidth, e.target.naturalHeight)}
                style={{ width: stageW, height: stageH, display: 'block', userSelect: 'none' }} draggable={false} />
            )}
            {rect && (
              <div onPointerDown={e => startDrag(e)}
                style={{ position: 'absolute', left: rect.x * scale, top: rect.y * scale, width: rect.w * scale, height: rect.h * scale, border: '1px solid #fff', boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)', cursor: 'move' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)', backgroundSize: '33.333% 33.333%', pointerEvents: 'none' }} />
                {handle('nw', { left: -7, top: -7 })}
                {handle('ne', { right: -7, top: -7 })}
                {handle('sw', { left: -7, bottom: -7 })}
                {handle('se', { right: -7, bottom: -7 })}
              </div>
            )}
          </div>
        </div>

        {isVideo && duration > 0 && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <TrimBar duration={duration} start={range.start} end={end} time={time}
              onSeek={seek} onStart={setStart} onEnd={setEnd} disabled={!!busy} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: C.muted }}>
              <button onClick={togglePlay} disabled={!!busy} style={btn('ghost', { fontSize: 12, padding: '4px 10px' })}>{playing ? '❚❚ Pause' : '▶ Play'}</button>
              <span style={{ fontFamily: F.mono }}>{formatTime(time)}</span>
              <button onClick={() => setStart(time)} disabled={!!busy} style={btn('ghost', { fontSize: 12, padding: '4px 10px' })}>Set start here</button>
              <button onClick={() => setEnd(time)} disabled={!!busy} style={btn('ghost', { fontSize: 12, padding: '4px 10px' })}>Set end here</button>
              <span>Start</span>
              <input type="number" step="0.1" min="0" value={range.start.toFixed(1)} onChange={e => setStart(Number(e.target.value) || 0)} disabled={!!busy} style={{ ...inp(), width: 70, fontSize: 12 }} />
              <span>End</span>
              <input type="number" step="0.1" min="0" value={end.toFixed(1)} onChange={e => setEnd(Number(e.target.value) || 0)} disabled={!!busy} style={{ ...inp(), width: 70, fontSize: 12 }} />
              <span style={{ marginLeft: 'auto' }}>Length <b style={{ color: C.text, fontFamily: F.mono }}>{formatTime(end - range.start)}</b> of {formatTime(duration)}</span>
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          {r && <>Crop <b style={{ color: C.text }}>{r.w}×{r.h}</b> px → output <b style={{ color: C.text }}>{out.w}×{out.h}</b> px</>}
          {upscaling && <span style={{ color: '#b45309' }}> · the crop is smaller than the output size, so it will be upscaled and may look soft</span>}
          {isVideo && <div>Videos are processed in the background and come out as mp4 — you can carry on working; progress shows in the corner. Keep this tab open until it finishes.</div>}
        </div>

        {error && <div style={{ marginTop: 10, fontSize: 12, color: '#dc2626', whiteSpace: 'pre-wrap' }}>{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          {busy && <span style={{ fontSize: 12, color: C.muted, marginRight: 'auto' }}>
            {busy === 'loading' ? 'Loading video tools…' : busy === 'encoding' ? `Processing video… ${Math.round(progress * 100)}%` : busy === 'uploading' ? 'Uploading…' : 'Cropping…'}
          </span>}
          <button onClick={onClose} disabled={!!busy} style={btn('ghost')}>Cancel</button>
          {canNext && (
            <button onClick={() => apply(true)} disabled={!!busy || !r || untouched} style={btn('secondary')}>
              Apply & next →
            </button>
          )}
          <button onClick={() => apply(false)} disabled={!!busy || !r || untouched} style={btn('primary')}>
            {isVideo ? 'Apply crop & trim' : 'Apply crop'}
          </button>
        </div>
      </div>
    </div>
  );
}

// "Crop all": centres one preset on every item so they can then be fine-tuned one by one —
// for a single product (Studio toolbar) or a whole collection (`groups` = one per product).
// Items already cropped to that preset are skipped, so re-running never undoes manual
// repositioning. Images run a few at a time; videos one at a time (single ffmpeg instance).
// Each group is handed to onGroupDone(id, { index: { file, crop } }) as soon as it finishes,
// so a long run saves as it goes. `modal` shows it as a blocking dialog (collection-wide,
// where an edit made mid-run could otherwise be overwritten).
export function BulkCrop({ groups, presets = [], onGroupDone, modal = false, label = 'Crop all…' }) {
  // Videos are queued in the background (utils/cropJobs) rather than run here; `label` on
  // each group names the product in the progress tray.
  const [open, setOpen]           = useState(false);
  const [presetId, setPresetId]   = useState(presets[0]?.id);
  const [withVideo, setWithVideo] = useState(false);
  const [status, setStatus]       = useState('');
  const [busy, setBusy]           = useState(false);
  const [errors, setErrors]       = useState([]);
  // Pixel size of every item, by URL — read once the panel opens so items that are already
  // the preset's shape can be left alone. Only metadata is loaded, so it's quick.
  const sizes = useRef({});
  const [measuring, setMeasuring] = useState(null);   // null | { done, total }
  const urlsKey = groups.flatMap(g => g.images.filter(m => withVideo || !isVideoMedia(m)).map(m => m.url)).join('|');
  useEffect(() => {
    if (!open) return;
    const todo = [...new Map(groups.flatMap(g => g.images)
      .filter(m => (withVideo || !isVideoMedia(m)) && !(m.url in sizes.current))
      .map(m => [m.url, m])).values()];
    if (!todo.length) return;
    let cancelled = false;
    (async () => {
      const count = { done: 0 };
      setMeasuring({ done: 0, total: todo.length });
      for (let k = 0; k < todo.length && !cancelled; k += 6) {
        await Promise.all(todo.slice(k, k + 6).map(async m => {
          sizes.current[m.url] = await mediaSize(m.url, isVideoMedia(m)).catch(() => null);
          count.done++;
        }));
        if (!cancelled) setMeasuring({ done: count.done, total: todo.length });
      }
      if (!cancelled) setMeasuring(null);
    })();
    return () => { cancelled = true; };
  }, [open, urlsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const anyMedia = groups.some(g => g.images.length);
  if (!presets.length || !anyMedia) return null;

  const preset = presets.find(p => p.id === presetId) || presets[0];
  const presetR = presetRatio(preset);
  const eligible = g => g.images
    .map((m, i) => ({ m, i, video: isVideoMedia(m) }))
    .filter(t => (withVideo || !t.video) && t.m.crop?.presetId !== preset.id);
  const targetsFor = g => eligible(g).filter(t => !fitsRatio(sizes.current[t.m.url], presetR));
  const total = groups.reduce((n, g) => n + targetsFor(g).length, 0);
  const alreadyFit = groups.reduce((n, g) => n + eligible(g).length, 0) - total;
  const productCount = groups.filter(g => targetsFor(g).length).length;
  const hasVideo = groups.some(g => g.images.some(isVideoMedia));

  const run = async () => {
    setBusy(true); setErrors([]);
    const ratio = presetRatio(preset);
    const errs = [];
    const count = { done: 0 };
    setStatus(`Cropping 0 / ${total}…`);
    for (const g of groups) {
      const targets = targetsFor(g);
      if (!targets.length) continue;
      const results = {};
      const one = async ({ m, i, video }) => {
        try {
          results[i] = await cropToPreset(m, preset, {
            isVideo: video, ratio,
            onProgress: p => setStatus(`Cropping ${count.done + 1} / ${total} — video ${Math.round(p * 100)}%`),
          });
        } catch (err) {
          errs.push(`${g.label ? `${g.label} — ` : ''}${(m.original || m).filename || 'item'}: ${err.message}`);
        }
        setStatus(`Cropping ${++count.done} / ${total}…`);
      };
      const stills = targets.filter(t => !t.video);
      for (let k = 0; k < stills.length; k += 3) await Promise.all(stills.slice(k, k + 3).map(one));
      for (const { m } of targets.filter(t => t.video)) {
        enqueueCrop({
          productId: g.id, mediaUrl: m.url,
          label: `${g.label || 'Video'} — ${(m.original || m).filename || 'video'}`,
          make: (cb) => cropToPreset(m, preset, { isVideo: true, ratio, onProgress: cb.onProgress }),
        });
        count.done++;
      }
      if (Object.keys(results).length) {
        try { await onGroupDone(g.id, results); }
        catch (err) { errs.push(`${g.label ? `${g.label}: ` : ''}${err.message}`); }
      }
    }
    setErrors(errs);
    setBusy(false);
    setStatus('');
    if (!errs.length) setOpen(false);
  };

  const trigger = (
    <button onClick={() => setOpen(true)}
      style={modal ? { ...btn('secondary'), fontSize: 13 } : { ...btn('ghost', { fontSize: 12, padding: '4px 10px' }) }}>{label}</button>
  );
  if (!open) return trigger;

  const panel = (
    <div style={{ ...(modal ? { width: 560, maxWidth: '94vw', background: C.white, borderRadius: 14, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }
                             : { flexBasis: '100%', padding: 12, background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10 }),
                  display: 'flex', flexDirection: 'column', gap: 10, fontFamily: F.body }}>
      {modal && <div style={{ fontFamily: F.display, fontSize: 18, color: C.text, marginBottom: 4 }}>Crop the whole collection</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: C.muted, marginRight: 4 }}>Crop all to</span>
        {presets.map(p => {
          const active = p.id === preset.id;
          return (
            <button key={p.id} onClick={() => setPresetId(p.id)} disabled={busy}
              style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px', border: `1px solid ${active ? C.text : C.e8}`, borderRadius: 6 }), background: active ? C.text : C.white, color: active ? C.white : C.text }}>
              {p.label}<span style={{ opacity: 0.6, marginLeft: 6 }}>{p.ratioW}:{p.ratioH}{Number(p.width) > 0 ? ` · ${p.width}px` : ''}</span>
            </button>
          );
        })}
      </div>
      {hasVideo && (
        <label style={{ fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={withVideo} onChange={e => setWithVideo(e.target.checked)} disabled={busy} />
          Include videos <span style={{ color: C.muted }}>(processed in the background one at a time — progress shows in the corner)</span>
        </label>
      )}
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        {measuring
          ? `Checking sizes… ${measuring.done} / ${measuring.total}`
          : total
            ? `Centres a ${preset.label} crop on ${total} item${total === 1 ? '' : 's'}${groups.length > 1 ? ` across ${productCount} product${productCount === 1 ? '' : 's'}` : ''}. Fine-tune any of them afterwards with Crop; Revert brings back the original.`
            : `Nothing to crop — everything is already ${preset.ratioW}:${preset.ratioH}.`}
        {!measuring && alreadyFit > 0 && (
          <div style={{ marginTop: 4 }}>{alreadyFit} already {alreadyFit === 1 ? 'is' : 'are'} {preset.ratioW}:{preset.ratioH} and will be left alone.</div>
        )}
        {modal && busy && <div style={{ marginTop: 4 }}>Each product saves as soon as it's done — keep this tab open until it finishes.</div>}
      </div>
      {errors.length > 0 && <div style={{ fontSize: 12, color: '#dc2626', whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{errors.length} failed:{'\n'}{errors.join('\n')}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: modal ? 6 : 0 }}>
        <button onClick={run} disabled={busy || !!measuring || !total} style={btn('primary', { fontSize: 12, padding: '6px 14px' })}>
          {busy ? status || 'Working…' : `Crop ${total}`}
        </button>
        <button onClick={() => { setOpen(false); setErrors([]); }} disabled={busy} style={btn('ghost', { fontSize: 12, padding: '6px 12px' })}>
          {errors.length ? 'Close' : 'Cancel'}
        </button>
      </div>
    </div>
  );

  if (!modal) return panel;
  return (
    <>
      {trigger}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {panel}
      </div>
    </>
  );
}

// Timeline with draggable start/end handles; clicking the track seeks the preview.
function TrimBar({ duration, start, end, time, onSeek, onStart, onEnd, disabled }) {
  const ref = useRef(null);
  const which = useRef(null);
  const tAt = (clientX) => {
    const b = ref.current.getBoundingClientRect();
    return Math.min(duration, Math.max(0, ((clientX - b.left) / b.width) * duration));
  };
  useEffect(() => {
    const move = e => {
      if (!which.current) return;
      const t = tAt(e.clientX);
      if (which.current === 'start') onStart(t); else onEnd(t);
      onSeek(t);
    };
    const up = () => { which.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  });
  const pct = t => `${(t / duration) * 100}%`;
  const grip = (kind, t) => (
    <div onPointerDown={e => { if (disabled) return; e.stopPropagation(); which.current = kind; }}
      style={{ position: 'absolute', left: pct(t), top: -4, bottom: -4, width: 10, marginLeft: -5, background: C.text, borderRadius: 3, cursor: 'ew-resize' }} />
  );
  return (
    <div ref={ref} onPointerDown={e => !disabled && onSeek(tAt(e.clientX))}
      style={{ position: 'relative', height: 22, background: C.e8, borderRadius: 4, cursor: 'pointer', touchAction: 'none' }}>
      <div style={{ position: 'absolute', left: pct(start), width: `${((end - start) / duration) * 100}%`, top: 0, bottom: 0, background: 'rgba(10,10,10,0.18)', borderTop: `2px solid ${C.text}`, borderBottom: `2px solid ${C.text}` }} />
      <div style={{ position: 'absolute', left: pct(time), top: -2, bottom: -2, width: 2, background: '#dc2626' }} />
      {grip('start', start)}
      {grip('end', end)}
    </div>
  );
}
